// Submissions panel — shared by candidate-detail (candidateId) and job-order-detail (jobOrderId).
// Submissions/Interviews are top-level endpoints filtered by query param (not nested routes).
import { Fragment, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { BulkModal } from "@/components/bulk-modal";
import { Plus, Trash2, ChevronDown, ChevronRight } from "lucide-react";

const SUBMISSION_STATUSES = ["SUBMITTED", "REVIEW", "INTERVIEW", "OFFER", "PLACED", "REJECTED"] as const;
const INTERVIEW_FORMATS = ["VIDEO", "PHONE", "ONSITE"] as const;
const INTERVIEW_OUTCOMES = ["PENDING", "PASS", "FAIL", "NO_SHOW", "CANCELLED"] as const;

const apiErr = (e: any) => e?.response?.data?.message ?? e?.response?.data?.error?.message ?? e?.message ?? "Something went wrong";
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : "—");
const lbl = (s: string) => s.replace(/_/g, " ");

interface Props {
  candidateId?: string;
  jobOrderId?: string;
}

export function StaffingSubmissionsPanel({ candidateId, jobOrderId }: Props) {
  const qc = useQueryClient();
  const mode = candidateId ? "candidate" : "jobOrder";
  const params = candidateId ? { candidateId } : { jobOrderId };
  const queryKey = ["/submissions", params];
  const [addOpen, setAddOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await api.get("/submissions", { params })).data.data as any[],
  });
  const rows = data ?? [];

  async function setStatus(id: string, status: string) {
    setErr(null);
    if (status === "PLACED" && !confirm("Mark PLACED? If the job order has bill & pay rates set, this auto-creates a Placement, marks the candidate PLACED, and sets the job order FILLED.")) return;
    try {
      await api.put(`/submissions/${id}`, { status });
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: ["/placements"] });
    } catch (e) {
      setErr(apiErr(e));
    }
  }
  async function remove(id: string) {
    if (!confirm("Remove this submission? Its interviews are deleted too.")) return;
    setErr(null);
    try {
      await api.delete(`/submissions/${id}`);
      qc.invalidateQueries({ queryKey });
    } catch (e) {
      setErr(apiErr(e));
    }
  }

  return (
    <div className="space-y-3">
      {err && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{err}</p>}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{rows.length} submission{rows.length === 1 ? "" : "s"}</div>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />{mode === "candidate" ? "Submit to job order" : "Submit a candidate"}
        </Button>
      </div>
      {isLoading && <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && rows.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No submissions yet.</p>}
      {rows.length > 0 && (
        <div className="rounded-md border">
          <Table>
            <Thead>
              <Tr>
                <Th className="w-8"></Th>
                <Th>{mode === "candidate" ? "Job Order" : "Candidate"}</Th>
                <Th>Status</Th>
                <Th>Submitted</Th>
                <Th>Interviews</Th>
                <Th className="w-10"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((r: any) => (
                <Fragment key={r.id}>
                  <Tr className="group">
                    <Td>
                      <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} aria-label="Toggle interviews">
                        {expanded === r.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </Td>
                    <Td className="font-medium">
                      {mode === "candidate"
                        ? <Link to={`/job-orders/${r.jobOrder?.id}`} className="hover:underline">{r.jobOrder?.title ?? "—"}</Link>
                        : <Link to={`/candidates/${r.candidate?.id}`} className="hover:underline">{r.candidate?.name ?? "—"}</Link>}
                    </Td>
                    <Td>
                      <Select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="h-7 w-36 text-xs">
                        {SUBMISSION_STATUSES.map((s) => <option key={s} value={s}>{lbl(s)}</option>)}
                      </Select>
                    </Td>
                    <Td className="text-sm text-muted-foreground">{fmtDate(r.submittedAt)}</Td>
                    <Td className="text-sm text-muted-foreground">{r.interviews?.length ?? 0}</Td>
                    <Td>
                      <button onClick={() => remove(r.id)} className="text-destructive opacity-0 group-hover:opacity-100" aria-label="Remove">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </Td>
                  </Tr>
                  {expanded === r.id && (
                    <Tr>
                      <Td colSpan={6} className="bg-muted/30">
                        <InterviewsSubpanel submissionId={r.id} onChange={() => qc.invalidateQueries({ queryKey })} />
                      </Td>
                    </Tr>
                  )}
                </Fragment>
              ))}
            </Tbody>
          </Table>
        </div>
      )}
      {addOpen && (
        <AddSubmissionModal
          mode={mode}
          candidateId={candidateId}
          jobOrderId={jobOrderId}
          onClose={() => setAddOpen(false)}
          onAdded={() => qc.invalidateQueries({ queryKey })}
        />
      )}
    </div>
  );
}

function InterviewsSubpanel({ submissionId, onChange }: { submissionId: string; onChange: () => void }) {
  const qc = useQueryClient();
  const queryKey = ["/interviews", submissionId];
  const [addOpen, setAddOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await api.get("/interviews", { params: { submissionId } })).data.data as any[],
  });
  const rows = data ?? [];
  async function setOutcome(id: string, outcome: string) {
    await api.put(`/interviews/${id}`, { outcome });
    qc.invalidateQueries({ queryKey });
  }
  async function remove(id: string) {
    if (!confirm("Delete this interview?")) return;
    await api.delete(`/interviews/${id}`);
    qc.invalidateQueries({ queryKey });
    onChange();
  }
  return (
    <div className="space-y-2 py-2">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Interviews</div>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAddOpen(true)}>
          <Plus className="mr-1 h-3 w-3" />Add interview
        </Button>
      </div>
      {isLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
      {!isLoading && rows.length === 0 && <p className="text-xs text-muted-foreground">No interviews scheduled.</p>}
      {rows.map((iv: any) => (
        <div key={iv.id} className="flex items-center gap-3 rounded border bg-card px-2 py-1 text-sm">
          <span className="font-medium">R{iv.round}</span>
          <Badge variant="secondary">{iv.format}</Badge>
          <span className="text-muted-foreground">{iv.scheduledAt ? new Date(iv.scheduledAt).toLocaleString() : "—"}</span>
          <Select value={iv.outcome} onChange={(e) => setOutcome(iv.id, e.target.value)} className="ml-auto h-7 w-32 text-xs">
            {INTERVIEW_OUTCOMES.map((o) => <option key={o} value={o}>{lbl(o)}</option>)}
          </Select>
          <button onClick={() => remove(iv.id)} className="text-destructive" aria-label="Delete interview"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      {addOpen && <AddInterviewModal submissionId={submissionId} onClose={() => setAddOpen(false)} onAdded={() => { qc.invalidateQueries({ queryKey }); onChange(); }} />}
    </div>
  );
}

function AddInterviewModal({ submissionId, onClose, onAdded }: { submissionId: string; onClose: () => void; onAdded: () => void }) {
  const [round, setRound] = useState(1);
  const [format, setFormat] = useState("VIDEO");
  const [scheduledAt, setScheduledAt] = useState("");
  const [outcome, setOutcome] = useState("PENDING");
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: async () => { await api.post("/interviews", { submissionId, round, format, scheduledAt, outcome }); },
    onSuccess: () => { onAdded(); onClose(); },
    onError: (e) => setErr(apiErr(e)),
  });
  return (
    <BulkModal open onClose={onClose} title="Schedule interview" confirmLabel="Add" confirmDisabled={!scheduledAt} busy={mut.isPending} error={err} onConfirm={() => mut.mutate()}>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label>Round</Label><Input type="number" min={1} value={round} onChange={(e) => setRound(Number(e.target.value))} /></div>
        <div className="space-y-1"><Label>Format</Label><Select value={format} onChange={(e) => setFormat(e.target.value)}>{INTERVIEW_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}</Select></div>
      </div>
      <div className="space-y-1"><Label>Scheduled at</Label><Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} /></div>
      <div className="space-y-1"><Label>Outcome</Label><Select value={outcome} onChange={(e) => setOutcome(e.target.value)}>{INTERVIEW_OUTCOMES.map((o) => <option key={o} value={o}>{lbl(o)}</option>)}</Select></div>
    </BulkModal>
  );
}

function AddSubmissionModal({ mode, candidateId, jobOrderId, onClose, onAdded }: { mode: string; candidateId?: string; jobOrderId?: string; onClose: () => void; onAdded: () => void }) {
  const [q, setQ] = useState("");
  const [pickId, setPickId] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const pickEntity = mode === "candidate" ? "job-orders" : "candidates";
  const { data, isFetching } = useQuery({
    queryKey: ["picker", pickEntity, q],
    queryFn: async () => (await api.get(`/${pickEntity}`, { params: { q, pageSize: 25 } })).data.data as any[],
  });
  const list = data ?? [];
  const mut = useMutation({
    mutationFn: async () => {
      const body = mode === "candidate" ? { candidateId, jobOrderId: pickId } : { candidateId: pickId, jobOrderId };
      await api.post("/submissions", body);
    },
    onSuccess: () => { onAdded(); onClose(); },
    onError: (e) => setErr(apiErr(e)),
  });
  return (
    <BulkModal open onClose={onClose} title={mode === "candidate" ? "Submit to a job order" : "Submit a candidate"} confirmLabel="Submit" confirmDisabled={!pickId} busy={mut.isPending} error={err} onConfirm={() => mut.mutate()}>
      <div className="space-y-1">
        <Label>Search {mode === "candidate" ? "job orders" : "candidates"}</Label>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type to search…" />
      </div>
      <div className="max-h-64 overflow-auto rounded-md border">
        {isFetching && <p className="p-3 text-sm text-muted-foreground">Loading…</p>}
        {!isFetching && list.length === 0 && <p className="p-3 text-sm text-muted-foreground">No matches.</p>}
        {list.map((it: any) => (
          <label key={it.id} className="flex cursor-pointer items-center gap-2 border-b px-3 py-1.5 text-sm last:border-0 hover:bg-muted">
            <input type="radio" name="pick" checked={pickId === it.id} onChange={() => setPickId(it.id)} />
            <span className="font-medium">{mode === "candidate" ? it.title : it.name}</span>
            <span className="ml-auto text-xs text-muted-foreground">{mode === "candidate" ? (it.clientAccount?.name ?? "") : (it.email ?? "")}</span>
          </label>
        ))}
      </div>
    </BulkModal>
  );
}
